/**
 * ConstructionWorkerModal Component
 * 
 * Modal component for adding or editing construction workers.
 * Similar to AddNewUserModal but with construction worker-specific features:
 * - Employee code is entered manually (4 digits) in add mode
 * - Supports both full-width and half-width Katakana in names
 * 
 * Features:
 * - Add mode: Manual 4-digit employee code, validates Japanese name, required password
 * - Edit mode: Employee code is disabled, password is optional
 * - Form validation with Japanese error messages
 * - Portal rendering to avoid parent container clipping
 * - Body scroll lock when modal is open
 * - Japanese language interface
 * 
 * Validation Rules:
 * - Employee Code: Must be exactly 4 digits, entered manually in add mode
 * - Name: Must be Japanese characters (Kanji, Hiragana, Katakana - full/half-width)
 * - Password: Minimum 6 characters, must contain at least one uppercase letter and one digit
 * - Password confirmation: Must match password
 * 
 * Differences from AddNewUserModal:
 * - Employee code format: 4 digits (manual entry) instead of 6-digit office codes
 * - Supports half-width Katakana in names
 * - User type is fixed to "construction worker"
 * 
 * Used by:
 * - UserManagementMain component for construction worker management
 * 
 * @component
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './AddNewUserModal.css';

/**
 * Props Interface for ConstructionWorkerModal Component
 * 
 * @interface ConstructionWorkerModalProps
 * @property {boolean} isOpen - Whether the modal is open
 * @property {() => void} onClose - Callback function to close the modal
 * @property {(userData: ConstructionWorkerData) => void} [onAddUser] - Optional callback for adding new user
 * @property {(userData: ConstructionWorkerData) => void} [onUpdateUser] - Optional callback for updating user
 * @property {'add' | 'edit'} [mode='add'] - Modal mode: 'add' for new user, 'edit' for existing user
 * @property {Partial<ConstructionWorkerData>} [initialData] - Optional initial data for edit mode
 */
interface ConstructionWorkerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddUser?: (userData: ConstructionWorkerData) => void | Promise<{ success: boolean; message?: string }>;
  onUpdateUser?: (userData: ConstructionWorkerData) => void | Promise<{ success: boolean; message?: string }>;
  mode?: 'add' | 'edit';
  initialData?: Partial<ConstructionWorkerData>;
}

/**
 * Construction Worker Data Interface
 * 
 * @interface ConstructionWorkerData
 * @property {string} empCode - Employee code (4 digits, e.g., 0001)
 * @property {string} name - Employee name (Japanese characters)
 * @property {string} userType - User type (fixed to 'construction worker')
 * @property {string} password - Password (minimum 6 characters with uppercase and digit)
 * @property {string} passwordConfirm - Password confirmation (must match password)
 */
interface ConstructionWorkerData {
  empCode: string;
  name: string;
  userType: string;
  password: string;
  passwordConfirm: string;
}

export const ConstructionWorkerModal: React.FC<ConstructionWorkerModalProps> = ({
  isOpen,
  onClose,
  onAddUser,
  onUpdateUser,
  mode = 'add',
  initialData
}) => {
  const [formData, setFormData] = useState<ConstructionWorkerData>({
    empCode: '',
    name: '',
    userType: 'construction worker',
    password: '',
    passwordConfirm: ''
  });

  const [errors, setErrors] = useState<Partial<ConstructionWorkerData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Maps backend error message to field errors in Japanese for display in the form.
   */
  const setErrorsFromBackendMessage = (message: string | undefined) => {
    if (!message) return;
    const m = message.toLowerCase();
    if (m.includes('employee code already exists') || m.includes('empcode') || m.includes('emp code')) {
      setErrors(prev => ({ ...prev, empCode: 'この従業員コードは既に使用されています。' }));
    } else if (
      m.includes('name already exists')
      || m.includes('this name')
      || m.includes('one account per person')
      || message.includes('同じ人を二重に登録')
      || message.includes('この名前の方は既に登録')
    ) {
      setErrors(prev => ({ ...prev, name: 'この名前の方は既に登録されています。同じ人を二重に登録することはできません。内容を確認してください。' }));
    } else {
      setErrors(prev => ({ ...prev, name: '登録に失敗しました。入力内容を確認してください。' }));
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && initialData) {
        setFormData(prev => ({
          empCode: initialData.empCode ?? prev.empCode,
          name: initialData.name ?? prev.name,
          userType: initialData.userType ?? prev.userType,
          password: '',
          passwordConfirm: ''
        }));
        setErrors({});
      } else if (mode === 'add') {
        setFormData({ empCode: '', name: '', userType: 'construction worker', password: '', passwordConfirm: '' });
        setErrors({});
      }
    }
  }, [isOpen, mode, initialData]);

  /**
   * Validates if the value is a valid construction worker employee code.
   * Format: Exactly 4 digits (e.g., 0001, 1234).
   */
  const isConstructionWorkerEmpCode = (value: string): boolean => {
    return /^\d{4}$/.test(value);
  };

  const isJapaneseName = (value: string): boolean => {
    // Kanji: \u4E00-\u9FFF, CJK Ext A: \u3400-\u4DBF, Hiragana: \u3040-\u309F, Katakana: \u30A0-\u30FF, Katakana Phonetic Ext: \u31F0-\u31FF
    // Half-width Katakana: \uFF65-\uFF9F, Common name marks/spaces: \u3000 (full-width space), \u3005 (々), \u30F5 (ヵ), \u30F6 (ヶ), \u30FB (・), \u30FC (ー), whitespace
    const japaneseNameRegex = /^[\u3040-\u309F\u30A0-\u30FF\u31F0-\u31FF\u4E00-\u9FFF\u3400-\u4DBF\uFF65-\uFF9F\u3000\u3005\u30F5\u30F6\u30FB\u30FC\s]+$/;
    return japaneseNameRegex.test(value);
  };

  const isPasswordValid = (value: string): boolean => {
    // At least one uppercase and one digit; letters and digits only
    return /^(?=.*[A-Z])(?=.*\d)[A-Za-z0-9]+$/.test(value);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ConstructionWorkerData> = {};

    if (mode === 'add') {
      if (!formData.empCode) newErrors.empCode = '従業員コードを入力してください（4桁の数字）';
      else if (!isConstructionWorkerEmpCode(formData.empCode)) newErrors.empCode = '従業員コードは4桁の数字で入力してください';

      if (!formData.name) newErrors.name = '名前は必須です';
      else if (!isJapaneseName(formData.name)) newErrors.name = '名前は漢字・ひらがな・カタカナ（全角・半角）のみ使用できます';

      if (!formData.userType) newErrors.userType = '従業員タイプは必須です';
      if (!formData.password) newErrors.password = 'パスワードは必須です';
      else if (formData.password.length < 6) newErrors.password = 'パスワードは6文字以上である必要があります';
      else if (!isPasswordValid(formData.password)) newErrors.password = 'パスワードは英数字のみで、少なくとも1つの大文字と数字を含めてください';
      if (formData.password !== formData.passwordConfirm) {
        newErrors.passwordConfirm = 'パスワードが一致しません';
      }
    } else {
      // edit mode: empCode is disabled and not editable; name required; userType required; password fields optional
      if (!formData.name) newErrors.name = '名前は必須です';
      else if (!isJapaneseName(formData.name)) newErrors.name = '名前は漢字・ひらがな・カタカナ（全角・半角）のみ使用できます';
      
      if (!formData.userType) newErrors.userType = '従業員タイプは必須です';
      const passwordProvided = formData.password.trim().length > 0 || formData.passwordConfirm.trim().length > 0;
      if (passwordProvided) {
        if (!formData.password) newErrors.password = '新しいパスワードを入力してください';
        else if (formData.password.length < 6) newErrors.password = 'パスワードは6文字以上である必要があります';
        else if (!isPasswordValid(formData.password)) newErrors.password = 'パスワードは英数字のみで、少なくとも1つの大文字と数字を含めてください';
        if (formData.password !== formData.passwordConfirm) {
          newErrors.passwordConfirm = 'パスワードが一致しません';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (mode === 'add' && onAddUser) {
      setIsSubmitting(true);
      setErrors({});
      try {
        const result = await Promise.resolve(onAddUser(formData));
        if (result && typeof result === 'object' && result.success === false) {
          setErrorsFromBackendMessage(result.message);
          return;
        }
        handleClear();
        onClose();
      } finally {
        setIsSubmitting(false);
      }
    } else if (mode === 'edit' && onUpdateUser) {
      setIsSubmitting(true);
      setErrors({});
      try {
        const result = await Promise.resolve(onUpdateUser(formData));
        if (result && typeof result === 'object' && result.success === false) {
          setErrorsFromBackendMessage(result.message);
          return;
        }
        onClose();
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleClear = () => {
    if (mode === 'edit') {
      setFormData({
        empCode: initialData?.empCode ?? '',
        name: initialData?.name ?? '',
        userType: initialData?.userType ?? 'construction worker',
        password: '',
        passwordConfirm: ''
      });
    } else {
      setFormData({
        empCode: '',
        name: '',
        userType: 'construction worker',
        password: '',
        passwordConfirm: ''
      });
    }
    setErrors({});
  };

  const handleCancel = () => {
    handleClear();
    onClose();
  };

  const handleInputChange = (field: keyof ConstructionWorkerData, value: string) => {
    // In add mode, restrict empCode to digits only and max 4 characters
    if (field === 'empCode' && mode === 'add') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 4);
      setFormData(prev => ({ ...prev, [field]: digitsOnly }));
      setErrors(prev => ({ ...prev, empCode: undefined }));
      return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current overflow style
      const originalOverflow = document.body.style.overflow;
      // Lock body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore original overflow when modal closes
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Render modal using portal to document.body to avoid parent container clipping
  return createPortal(
    <div className="modal-overlay">
      <div className="modal-content">
        {/* Header */}
        <div className="modal-header">
          <h3>{mode === 'edit' ? '現場編集' : '新規現場追加'}</h3>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="add-user-form">
          <div className="form-group">
            <label htmlFor="empCode">従業員コード *</label>
            <input
              type="text"
              id="empCode"
              inputMode="numeric"
              value={formData.empCode}
              placeholder={mode === 'edit' ? "編集時は変更できません" : "4桁の数字を入力"}
              maxLength={4}
              title={mode === 'edit' ? "編集時は変更できません" : "4桁の数字を入力してください"}
              className={errors.empCode ? 'error' : ''}
              disabled={mode === 'edit'}
              readOnly={mode === 'edit'}
              onChange={(e) => handleInputChange('empCode', e.target.value)}
            />
            {mode === 'edit' && formData.empCode && <span className="info-message">編集時は従業員コードは変更できません</span>}
            {errors.empCode && <span className="error-message">{errors.empCode}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="name">名前 *</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="フルネームを入力"
              title="漢字・ひらがな・カタカナ（全角・半角）のみ使用できます"
              className={errors.name ? 'error' : ''}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="userType">従業員タイプ *</label>
            <select
              id="userType"
              value={formData.userType}
              onChange={(e) => handleInputChange('userType', e.target.value)}
              className={errors.userType ? 'error' : ''}
            >
              <option value="construction worker">従業員</option>
            </select>
            {errors.userType && <span className="error-message">{errors.userType}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">パスワード *</label>
            <input
              type="password"
              id="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              placeholder={mode === 'edit' ? '新しいパスワード（任意）' : 'パスワードを入力'}
              pattern="[A-Za-z0-9]{6,}"
              title="英数字のみ。6文字以上で、少なくとも1つの大文字と数字を含む必要があります"
              className={errors.password ? 'error' : ''}
            />
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="passwordConfirm">パスワード再入力 *</label>
            <input
              type="password"
              id="passwordConfirm"
              value={formData.passwordConfirm}
              onChange={(e) => handleInputChange('passwordConfirm', e.target.value)}
              placeholder={mode === 'edit' ? '新しいパスワードを再入力（任意）' : 'パスワードを再入力'}
              className={errors.passwordConfirm ? 'error' : ''}
            />
            {errors.passwordConfirm && <span className="error-message">{errors.passwordConfirm}</span>}
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleClear}>
              {mode === 'edit' ? 'リセット' : 'クリア'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>
              キャンセル
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? '送信中...' : (mode === 'edit' ? '更新' : '追加')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body // Render directly to body, outside component tree
  );
};
